import axios from 'axios';
import config from '../config';
import { getDriveApiToken } from '../lib/auth-token';
import { maybeBeginDriveReauthOn401 } from '../lib/drive-auth-api';

// Create API instance with base URL from config
const api = axios.create({
  baseURL: config.server.baseUrl,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  }
});

// Helper to determine content type based on data
const getContentType = (data) => {
  if (data instanceof FormData) {
    return undefined;
  }
  if (data instanceof File || data instanceof Blob) {
    return 'application/octet-stream';
  }
  return 'application/json';
};

// Request interceptor for adding auth token
api.interceptors.request.use(
  (reqConfig) => {
    const token = getDriveApiToken();
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    
    if (!reqConfig.headers['Content-Type'] && reqConfig.data) {
      const contentType = getContentType(reqConfig.data);
      if (contentType) {
        reqConfig.headers['Content-Type'] = contentType;
      }
    }
    
    reqConfig.headers['X-Request-ID'] = Math.random().toString(36).substring(2, 15);
    
    return reqConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — 401 session expired → One resume; never /login or / bounce
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const url = String(error.config?.url || '');
      const hadBearer = Boolean(
        error.config?.headers?.Authorization || getDriveApiToken()
      );
      maybeBeginDriveReauthOn401({
        path: url,
        status: 401,
        body: error.response?.data,
        hadBearer
      });
    }
    
    return Promise.reject(error);
  }
);

// Wrapper functions for common API operations
const apiService = {
  // Basic CRUD operations with enhanced error handling
  get: async (url, config = {}) => {
    try {
      return await api.get(url, config);
    } catch (error) {
      throw error;
    }
  },
  
  post: async (url, data = {}, config = {}) => {
    try {
      return await api.post(url, data, config);
    } catch (error) {
      throw error;
    }
  },
  
  put: async (url, data = {}, config = {}) => {
    try {
      return await api.put(url, data, config);
    } catch (error) {
      throw error;
    }
  },
  
  delete: async (url, config = {}) => {
    try {
      return await api.delete(url, config);
    } catch (error) {
      throw error;
    }
  },
  
  // File operations
  uploadFile: (file, folderId, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
      formData.append('folderId', folderId);
    }
    
    return api.post('/files/upload', formData, {
      timeout: config.upload.requestTimeoutMs,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        if (onProgress) onProgress(percentCompleted);
      },
    });
  },
  
  // Folder operations
  downloadFolderAsZip: (folderId) => {
    return api.get(`/folders/${folderId}/download`, {
      responseType: 'blob',
    });
  },
  
  prepareFolderDownload: (folderId) => {
    return api.post(`/folders/${folderId}/download/prepare`);
  },
  
  checkDownloadProgress: (downloadId) => {
    return api.get(`/folders/download/${downloadId}/progress`);
  },
  
  cancelDownload: (downloadId) => {
    return api.post(`/folders/download/${downloadId}/cancel`);
  },
  
  // Share operations
  getShareAnalytics: (shareId) => {
    return api.get(`/share/${shareId}/analytics`);
  },
  
  // Cloud operations
  connectCloudProvider: (providerId) => {
    return api.post(`/cloud/connect/${providerId}`);
  },
  
  getCloudAuthUrl: (providerId) => {
    return api.get(`/cloud/auth/${providerId}/url`);
  },
  
  // Transcoding operations
  transcodeFile: (fileId, options) => {
    return api.post(`/files/${fileId}/transcode`, options);
  },
  
  // Deduplication operations
  checkDuplicates: (fileHash) => {
    return api.get(`/files/duplicates/${fileHash}`);
  },
  
  // Collaborative editing
  getCollaborationSession: (fileId) => {
    return api.get(`/collaboration/session/${fileId}`);
  },
  
  // GraphQL operations
  graphql: (query, variables = {}) => {
    return api.post('/graphql', {
      query,
      variables,
    });
  }
};

export default apiService; 